import { useEffect, useMemo, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, PackageCheck, XCircle, Truck } from "lucide-react";
import { Link } from "react-router-dom";

function iconFor(status) {
  if (status === "delivered") return { i: PackageCheck, tone: "bg-accent/15 text-accent" };
  if (["cancelled", "returned", "refunded"].includes(status)) return { i: XCircle, tone: "bg-destructive/10 text-destructive" };
  return { i: Truck, tone: "bg-primary/10 text-primary" };
}

const labelFor = (status) => {
  const map = {
    assigned: "New order assigned",
    out_for_delivery: "You're now out for delivery",
    delivered: "Delivery completed",
    cancelled: "Order cancelled",
    returned: "Order returned",
    refunded: "Order refunded",
    picked_up: "You picked up an order",
    in_transit: "In transit",
  };
  return map[status] || `Update: ${status?.replace(/_/g, " ")}`;
};

export default function Notifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/assigned-to-me?limit=100")
      .then((r) => setRows(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Flatten timeline entries relevant to delivery partner
  const notifs = useMemo(() => {
    const out = [];
    rows.forEach((o) => {
      (o.timeline || []).forEach((t) => {
        if (["assigned", "out_for_delivery", "delivered", "cancelled", "returned", "picked_up"].includes(t.status)) {
          out.push({
            id: `${o.id}-${t.status}-${t.at}`,
            order_id: o.id,
            order_number: o.order_number,
            status: t.status,
            at: t.at,
            note: t.note,
          });
        }
      });
    });
    out.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
    return out.slice(0, 50);
  }, [rows]);

  return (
    <div className="p-4" data-testid="delivery-notifications-page">
      <div className="mb-4">
        <h1 className="text-2xl font-display font-semibold">Notifications</h1>
        <p className="text-xs text-muted-foreground mt-1">Alerts for your assigned orders</p>
      </div>

      {loading && <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>}

      {!loading && notifs.length === 0 && (
        <Card className="card-soft border-transparent" data-testid="notifs-empty">
          <CardContent className="p-8 text-center">
            <Bell className="w-10 h-10 mx-auto text-muted-foreground opacity-40 mb-2" />
            <div className="font-medium">You're all caught up</div>
            <div className="text-sm text-muted-foreground">No alerts right now.</div>
          </CardContent>
        </Card>
      )}

      {!loading && notifs.map((n) => {
        const { i: Icon, tone } = iconFor(n.status);
        return (
          <Link key={n.id} to={`/delivery/order/${n.order_id}`} data-testid={`notif-${n.id}`}>
            <Card className="card-soft border-transparent mb-2 hover:shadow-md transition">
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${tone}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium">{labelFor(n.status)}</div>
                    <Badge variant="secondary" className="text-[10px] font-mono">{n.order_number}</Badge>
                  </div>
                  {n.note && <div className="text-xs text-muted-foreground mt-0.5 italic">{n.note}</div>}
                  <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(n.at)}</div>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}

      <div className="text-[10px] text-muted-foreground text-center pt-4 flex items-center justify-center gap-1">
        <Bell className="w-3 h-3" /> Push notifications will go live once FCM is wired.
      </div>
    </div>
  );
}
